import * as Sentry from '@sentry/react'
import {Cart} from 'apps/Cart'
import {CartItemBuilder} from 'apps/CartItemBuilder/'
import {CartPreview} from 'apps/CartPreview'
import {Checkout} from 'apps/Checkout'
import {Confirming} from 'apps/Confirming/Confirming'
import {Order} from 'apps/Order'
import {RootProvider} from 'providers/RootProvider'
import React, {ReactNode} from 'react'
import ReactDOM from 'react-dom/client'
import './index.css'

declare global {
  interface Window {
    showCartItemBuilder: (containerId: string, productId: string, optionId?: string) => void
    showCart: (containerId: string) => void
    showCheckout: (containerId: string) => void
    showCartPreview: (containerId: string) => void
    showConfirming: (containerId: string) => void
    showOrder: (containerId: string) => void
    exp1SentryInitialised: boolean
  }
}

window.exp1SentryInitialised = false

const startApp = (containerId: string, app: ReactNode) => {
  const containerElement = document.getElementById(containerId)

  if (!containerElement) {
    throw new Error(`Can't find container element`)
  }

  if (!window.exp1SentryInitialised) {
    Sentry.init({
      dsn: 'https://edd1958775578c86aa1a325978a3d31b@o4508998825213952.ingest.us.sentry.io/4508998826917888',

      integrations: [
        Sentry.replayIntegration({
          maskAllInputs: false,
          maskAllText: false,
        }),
        Sentry.browserTracingIntegration(),
      ],

      // Session Replay
      replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.

      // Tracing
      tracesSampleRate: 0.1,
    })

    window.exp1SentryInitialised = true
  }

  const rootElement = document.createElement('div')
  rootElement.classList.add('dcw')
  containerElement.appendChild(rootElement)

  const root = ReactDOM.createRoot(rootElement)

  root.render(
    <React.StrictMode>
      <RootProvider root={root} rootElement={rootElement}>
        <Sentry.ErrorBoundary showDialog={false}>{app}</Sentry.ErrorBoundary>
      </RootProvider>
    </React.StrictMode>
  )
}

window.showCartItemBuilder = (containerId: string, productId: string, optionId?: string) => {
  startApp(containerId, <CartItemBuilder productId={productId} optionId={optionId} />)
}

window.showCart = (containerId: string) => {
  startApp(containerId, <Cart />)
}

window.showCheckout = (containerId: string) => {
  startApp(containerId, <Checkout />)
}

window.showCartPreview = (containerId: string) => {
  startApp(containerId, <CartPreview />)
}

window.showConfirming = (containerId: string) => {
  startApp(
    containerId,
    <Confirming paymentIntentId={new URLSearchParams(window.location.search).get('payment_intent') || ''} />
  )
}

window.showOrder = (containerId: string) => {
  startApp(containerId, <Order />)
}
